import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  useLead,
  useLeadActivities,
  useLeadRemarks,
  useMasterSources,
  useLeadMutations
} from '../../../hooks/useLeadQueries';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  ArrowLeft,
  Phone,
  Mail,
  MessageSquare,
  Calendar,
  MapPin,
  Building,
  User,
  Tag,
  Clock,
  Target,
  AlertCircle,
  FileText,
  Video,
  CheckCircle,
  XCircle,
  Send,
  X,
  Plus,
  Link as LinkIcon,
  Edit,
  Paperclip,
  Trash2,
  Upload,
  Download
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import leadService from '../../../api/crm/leads/leadService';
import {
  sanitizeTextInput,
  sanitizeSearchInput,
  handleTextInputChange,
  getCharacterCount,
  FIELD_LIMITS
} from '../../../utils/inputValidation';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import stringUtils from '../../../utils/stringUtils';
import PermissionGate from '../../../components/common/PermissionGate';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../../context/AuthContext';
import { patch, get, post, del } from '../../../api/apiClient';
import { API_ENDPOINTS } from '../../../api/config';
import { getLeadStatusColor, formatRoleName } from '../../../utils/constants';

// Map backend source to frontend display
const mapSourceToDisplay = (source, masterSources = []) => {
  if (!source) return 'N/A';
  if (typeof source === 'object') {
    return source.name || source.label || source.id || 'N/A';
  }

  const sourceStr = String(source).trim();
  if (Array.isArray(masterSources) && masterSources.length > 0) {
    const masterSource = masterSources.find(s =>
      String(s._id) === sourceStr ||
      String(s.id) === sourceStr ||
      (s.leadSourceCode && s.leadSourceCode.toLowerCase() === sourceStr.toLowerCase()) ||
      (s.name && s.name.toLowerCase() === sourceStr.toLowerCase())
    );
    if (masterSource) return masterSource.name;
  }

  const sourceMap = {
    'website': 'Website',
    'facebook': 'Facebook',
    'instagram': 'Instagram',
    'linkedin': 'LinkedIn',
    'google_ads': 'Google Ads',
    'whatsapp': 'WhatsApp',
    'referral': 'Referral',
    'cold_call': 'Cold Call',
    'walk_in': 'Walk In',
    'manual_entry': 'Manual Entry',
    'other': 'Other'
  };

  const normalizedKey = sourceStr.toLowerCase();
  return sourceMap[normalizedKey] || sourceStr || 'N/A';
};

// Helper function to map business type to industry display
// const mapBusinessTypeToIndustry = (businessType) => {
//   const typeMap = {
//     // Standard Mappings
//     'pharmaceuticals': 'Pharmaceuticals',
//     'healthcare': 'Healthcare',
//     'medical_devices': 'Medical Devices',
//     'biotechnology': 'Biotechnology',
//     'chemicals': 'Chemicals',
//     'manufacturing': 'Manufacturing',
//     'retail': 'Retail',
//     'wholesale': 'Wholesale',
//     'other': 'Other',

//     // Legacy/Alias Mappings
//     'pharmacy': 'Pharmaceuticals',
//     'hospital': 'Healthcare',
//     'clinic': 'Healthcare',
//     'retailer': 'Retail',
//     'distributor': 'Wholesale'
//   };
//   return typeMap[businessType] || businessType || 'Other';
// };

// Helper function to map employee count to company size display
const mapEmployeeCountToCompanySize = (employeeCount) => {
  if (!employeeCount) return 'Not specified';
  if (employeeCount <= 10) return 'Startup (1-10 employees)';
  if (employeeCount <= 50) return 'Small (10-50 employees)';
  if (employeeCount <= 200) return 'Medium (50-200 employees)';
  if (employeeCount <= 1000) return 'Large (200-1000 employees)';
  return 'Enterprise (1000+ employees)';
};

// Helper function to get lead name
const getLeadName = (lead) => {
  if (!lead) return 'Unknown Lead';
  if (lead.firstName && lead.lastName) {
    return `${lead.firstName} ${lead.lastName}`;
  } else if (lead.firstName) {
    return lead.firstName;
  } else if (lead.lastName) {
    return lead.lastName;
  } else {
    return 'Unknown Lead';
  }
};

// Helper function to render text with clickable links
const renderRemarkText = (text) => {
  if (!text) return null;
  if (typeof text !== 'string') return text;
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      const href = part.match(/^www\./i) ? `https://${part}` : part;
      return (
        <a
          key={index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-agrosaf-green hover:underline break-all font-medium"
        >
          {part}
        </a>
      );
    }
    return part;
  });
};


// Helper function to get the correct activity time
const getActivityTime = (activity) => {
  if (activity.performedAt) {
    return new Date(activity.performedAt).toISOString();
  }

  if (activity.isAutomated && activity.callMetadata) {
    const callTime = activity.callMetadata.startTime ||
      activity.callMetadata.legBStartTime ||
      activity.callMetadata.legAPickedTime ||
      activity.callMetadata.legBPickedTime;

    if (callTime) {
      try {
        const date = new Date(callTime);
        if (!isNaN(date.getTime())) {
          return date.toISOString();
        }
      } catch (error) {
        // Removed log
      }
    }
  }

  return activity.createdAt || new Date().toISOString();
};

// Helper function to format date with seconds
const formatDate = (dateString, options = {}) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    const formatOptions = {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    };

    if (options.includeTime !== false) {
      formatOptions.hour = '2-digit';
      formatOptions.minute = '2-digit';
      formatOptions.hour12 = true;
    }

    if (options.includeSeconds) {
      formatOptions.second = '2-digit';
    }

    return date.toLocaleDateString('en-IN', formatOptions);
  } catch (error) {
    return 'N/A';
  }
};

const getScoreColor = (score) => {
  switch (score) {
    case 'Hot': return 'bg-red-100 text-red-800';
    case 'Warm': return 'bg-yellow-100 text-yellow-800';
    case 'Cold': return 'bg-blue-100 text-blue-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getStatusColor = (status) => {
  return getLeadStatusColor(status);
};

const getActivityIcon = (type) => {
  switch (type) {
    case 'call': return Phone;
    case 'email': return Mail;
    case 'whatsapp': return MessageSquare;
    case 'meeting': return Calendar;
    case 'note': return FileText;
    default: return Clock;
  }
};

const getActivityColor = (type) => {
  switch (type) {
    case 'call': return 'text-blue-600';
    case 'email': return 'text-green-600';
    case 'whatsapp': return 'text-green-500';
    case 'meeting': return 'text-purple-600';
    case 'note': return 'text-gray-600';
    default: return 'text-gray-600';
  }
};

const getActivityBgColor = (type) => {
  switch (type) {
    case 'call': return 'bg-blue-100';
    case 'email': return 'bg-green-100';
    case 'whatsapp': return 'bg-green-100';
    case 'meeting': return 'bg-purple-100';
    case 'note': return 'bg-gray-100';
    default: return 'bg-gray-100';
  }
};

const getCallStatusDisplay = (status) => {
  if (!status) return null;
  const statusUpper = status.toUpperCase();
  const statusMap = {
    'ANSWER': 'Answered',
    'PICKED': 'Answered',
    'NOANSWER': 'Missed / No Answer',
    'BUSY': 'Busy',
    'CANCEL-AGENT': 'Cancelled by Agent',
    'CANCEL-CUSTOMER': 'Cancelled by Customer',
    'FAILED': 'Failed',
    'PENDING': 'Pending'
  };
  return statusMap[statusUpper] || status;
};

const getCallStatusColor = (status) => {
  if (!status) return 'bg-gray-100 text-gray-800';
  const statusUpper = status.toUpperCase();
  switch (statusUpper) {
    case 'ANSWER': return 'bg-green-100 text-green-800';
    case 'PICKED': return 'bg-green-100 text-green-800';
    case 'NOANSWER': return 'bg-yellow-100 text-yellow-800';
    case 'BUSY': return 'bg-orange-100 text-orange-800';
    case 'CANCEL-AGENT': return 'bg-red-100 text-red-800';
    case 'CANCEL-CUSTOMER': return 'bg-red-100 text-red-800';
    case 'FAILED': return 'bg-red-100 text-red-800';
    case 'PENDING': return 'bg-blue-100 text-blue-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const formatDuration = (minutes, callDurationSeconds = null) => {
  if (callDurationSeconds !== null && callDurationSeconds !== undefined) {
    const totalSeconds = Math.floor(callDurationSeconds);
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    if (hours > 0) {
      if (mins === 0 && secs === 0) return `${hours} hour${hours !== 1 ? 's' : ''}`;
      if (secs === 0) return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`;
      return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
    } else if (mins > 0) {
      if (secs === 0) return `${mins} minute${mins !== 1 ? 's' : ''}`;
      return `${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
    } else {
      return `${secs} second${secs !== 1 ? 's' : ''}`;
    }
  }

  if (!minutes) return '';
  const totalSeconds = Math.floor(minutes * 60);
  const hours = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  if (hours > 0) {
    if (mins === 0 && secs === 0) return `${hours} hour${hours !== 1 ? 's' : ''}`;
    if (secs === 0) return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`;
    return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
  } else if (mins > 0) {
    if (secs === 0) return `${mins} minute${mins !== 1 ? 's' : ''}`;
    return `${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
  } else {
    return `${secs} second${secs !== 1 ? 's' : ''}`;
  }
};

const getPerformerName = (performedBy) => {
  if (!performedBy) return 'Unknown User';
  if (typeof performedBy === 'string') return performedBy;
  if (performedBy.firstName && performedBy.lastName) {
    return `${performedBy.firstName} ${performedBy.lastName}`;
  } else if (performedBy.firstName) {
    return performedBy.firstName;
  } else if (performedBy.email) {
    return performedBy.email;
  }
  return 'Unknown User';
};

const LeadDetail360 = ({ onBack, onEdit, leadId: propLeadId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id: paramId } = useParams();
  const id = propLeadId || paramId;
  const [searchParams, setSearchParams] = useSearchParams();

  // React Query Hooks
  const {
    data: leadData,
    isLoading: leadLoading,
    error: leadError,
    refetch: refetchLead
  } = useLead(id);

  const {
    data: activitiesData,
    isLoading: activitiesLoading,
    fetchNextPage: loadMoreActivities,
    hasNextPage: activitiesHasMore,
    isFetchingNextPage: activitiesLoadingMore
  } = useLeadActivities(id);

  const { data: masterSources = [] } = useMasterSources();
  const leadMutations = useLeadMutations();


  // Dynamic lead statuses
  const [leadStatuses, setLeadStatuses] = useState([]);
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const result = await get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_STATUSES_DROPDOWN}`);
        if (result.success) {
          setLeadStatuses(result.data || []);
        }
      } catch (error) {
        // silent
      }
    };
    fetchStatuses();
  }, []);

  const getStatusDisplay = (stage) => {
    if (!stage) return 'New';
    const stageValue = typeof stage === 'string' ? stage : (stage.value || '');
    const found = leadStatuses.find(s => s.value === stageValue);
    return found ? found.name : stageValue.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Flatten infinite query activities
  const activities = useMemo(() => {
    return activitiesData?.pages.flatMap(page => page.activities) || [];
  }, [activitiesData]);

  // KPI Calculations
  const lastContactDate = useMemo(() => {
    const contactActivity = activities.find(a => ['call', 'email', 'meeting', 'whatsapp'].includes(a.type));
    return contactActivity ? (contactActivity.createdAt || getActivityTime(contactActivity)) : null;
  }, [activities]);

  const nextFollowUpDate = useMemo(() => {
    const futureActivities = activities
      .filter(a => a.scheduledFor && new Date(a.scheduledFor) > new Date())
      .sort((a, b) => new Date(a.scheduledFor) - new Date(b.scheduledFor));
    return futureActivities.length > 0 ? futureActivities[0].scheduledFor : null;
  }, [activities]);

  const latestFollowUpStatus = useMemo(() => {
    if (activities.length === 0) return 'N/A';
    const latest = activities[0];
    return latest.activityStatus?.name || latest.outcome || latest.status || latest.type || 'N/A';
  }, [activities]);

  const lostReasonData = useMemo(() => {
    if (leadData?.stage === 'closed_lost' && Array.isArray(leadData?.stageHistory)) {
      const lostHistory = leadData.stageHistory.filter(h => {
        if (!h) return false;
        const isLostStage = h.stage === 'closed_lost' ||
          (h.toStage && (h.toStage.value === 'closed_lost' || h.toStage === 'closed_lost'));
        return isLostStage && h.reason && h.reason.trim() !== '';
      });

      if (lostHistory.length > 0) {
        const sorted = [...lostHistory].sort((a, b) => {
          const dateA = a.changedAt ? new Date(a.changedAt) : new Date(0);
          const dateB = b.changedAt ? new Date(b.changedAt) : new Date(0);
          return dateB - dateA;
        });

        return {
          reason: sorted[0].reason,
          date: sorted[0].changedAt
        };
      }
    }
    return null;
  }, [leadData]);
  const statusHistory = useMemo(() => {
    if (!Array.isArray(leadData?.stageHistory)) return [];
    return [...leadData.stageHistory].sort((a, b) => {
      const aTime = new Date(a?.changedAt || 0).getTime();
      const bTime = new Date(b?.changedAt || 0).getTime();
      return bTime - aTime;
    });
  }, [leadData]);

  // Get initial tab from URL, default to 'profile'
  const initialTab = searchParams.get('tab') || 'profile';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Paginated remarks via backend — only fetch when remarks tab is active
  const {
    data: remarksData,
    isLoading: remarksLoading,
    fetchNextPage: loadMoreRemarks,
    hasNextPage: remarksHasMore,
    isFetchingNextPage: remarksLoadingMore
  } = useLeadRemarks(id, 10, activeTab === 'remarks');

  // Flatten infinite query remarks pages
  const paginatedRemarks = useMemo(() => {
    return remarksData?.pages.flatMap(page => page.remarks) || [];
  }, [remarksData]);

  const totalRemarksCount = useMemo(() => {
    return remarksData?.pages?.[0]?.pagination?.totalRemarks || 0;
  }, [remarksData]);

  const [newRemark, setNewRemark] = useState('');
  const [isSubmittingRemark, setIsSubmittingRemark] = useState(false);
  const [remarkFile, setRemarkFile] = useState(null);
  const [isUploadingRemarkFile, setIsUploadingRemarkFile] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [rejectVideoDocumentIndex, setRejectVideoDocumentIndex] = useState(null);
  const [videoRejectionReason, setVideoRejectionReason] = useState('');

  const getDocumentDownloadFilename = (doc, legacyData = null) => {
    const pdfData = legacyData || doc?.companyPdf;
    if (!pdfData) return "document.pdf";

    // If companyPdf is the metadata object
    if (typeof pdfData === 'object' && pdfData !== null) {
      const fallbackName = pdfData.originalName || pdfData.name || doc?.documentTypeName || doc?.documentType || "document";
      if (/\.[a-zA-Z0-9]+$/.test(fallbackName)) return fallbackName;

      const mimeType = (pdfData.mimetype || pdfData.mimeType || "").toLowerCase();
      if (mimeType === "application/pdf") return `${fallbackName}.pdf`;
      return `${fallbackName}.pdf`;
    }

    // Fallback for string URLs
    const pdfUrl = String(pdfData || '');
    const parts = pdfUrl.split('/');
    const filenameFromUrl = parts[parts.length - 1] || "document.pdf";
    const baseName = doc?.documentTypeName || doc?.documentType || filenameFromUrl;

    if (/\.[a-zA-Z0-9]+$/.test(baseName)) return baseName;
    return `${baseName.toLowerCase().endsWith('.pdf') ? baseName : baseName + '.pdf'}`;
  };

  const handleDocumentDownload = async (doc, legacyData = null) => {
    const pdfData = legacyData || doc?.companyPdf;
    if (!pdfData) return;

    const url = typeof pdfData === 'object' && pdfData !== null ? pdfData.url : pdfData;
    const filename = getDocumentDownloadFilename(doc, legacyData);

    try {
      const downloadToast = toast.loading('Preparing download...');

      const response = await authFetch(url);
      if (!response.ok) throw new Error("Failed to fetch document");

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
      toast.dismiss(downloadToast);
    } catch (error) {
      // Fallback: Just open in new tab if blob download fails
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleApproveVideoVerification = async (documentIndex) => {
    try {
      const response = await patch(`/crm/leads/${id}/documents/${documentIndex}/approve-video`);
      if (response?.responseCode !== 200) {
        throw new Error(response?.responseMessage || 'Failed to approve video verification');
      }
      toast.success('Video verification approved');
      refetchLead();
    } catch (error) {
      toast.error(error.message || 'Failed to approve video verification');
    }
  };

  const handleRejectVideoVerification = async () => {
    if (rejectVideoDocumentIndex === null) return;

    try {
      const response = await patch(
        `/crm/leads/${id}/documents/${rejectVideoDocumentIndex}/reject-video`,
        { rejectionReason: videoRejectionReason }
      );

      if (response?.responseCode !== 200) {
        throw new Error(response?.responseMessage || 'Failed to reject video verification');
      }

      toast.success('Video verification rejected');
      setRejectVideoDocumentIndex(null);
      setVideoRejectionReason('');
      refetchLead();
    } catch (error) {
      toast.error(error.message || 'Failed to reject video verification');
    }
  };

  // Update URL when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
  };

  // Sync activeTab with URL params on mount and when URL changes
  useEffect(() => {
    if (!isMountedRef.current) return;

    const tabFromUrl = searchParams.get('tab');
    const validTabs = ['profile', 'timeline', 'assignment_history', 'remarks', 'documents', 'status_history'];

    if (tabFromUrl && validTabs.includes(tabFromUrl)) {
      // Only update if different to avoid unnecessary re-renders
      if (activeTab !== tabFromUrl) {
        setActiveTab(tabFromUrl);
      }
      tabInitializedRef.current = true;
    } else if (!tabFromUrl && !tabInitializedRef.current) {
      // If no tab in URL and not yet initialized, set default and update URL (only once)
      setActiveTab('profile');
      setSearchParams({ tab: 'profile' }, { replace: true });
      tabInitializedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Only depend on searchParams to avoid loops

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showStatusHistoryModal, setShowStatusHistoryModal] = useState(false);
  const [selectedStatusHistory, setSelectedStatusHistory] = useState(null);
  const [notification, setNotification] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailHtml, setEmailHtml] = useState('');
  const [meetingTitle, setMeetingTitle] = useState('');
  const [meetingNotes, setMeetingNotes] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingTime, setMeetingTime] = useState('');
  const [meetingDuration, setMeetingDuration] = useState('00:30');
  const [meetingLink, setMeetingLink] = useState('');
  const [meetingType, setMeetingType] = useState('video');
  const [meetingErrors, setMeetingErrors] = useState({});
  const STATUS_REASON_PREVIEW_LIMIT = 80;

  // Use refs to track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  const notificationTimeoutRef = useRef(null);
  const tabInitializedRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);









  const showNotification = (message, type = 'success') => {
    if (!isMountedRef.current) return;

    // Clear any existing timeout
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }

    setNotification({ message, type });
    notificationTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setNotification(null);
      }
      notificationTimeoutRef.current = null;
    }, 3000);
  };

  // Show loading state
  if (leadLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading lead details...</p>
        </div>
      </div>
    );
  }

  // Show error state if lead not found or error occurred
  if (leadError || !leadData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="p-3 bg-red-100 rounded-full mb-4 mx-auto w-fit">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Lead Not Found</h3>
          <p className="text-gray-600 mb-4">The lead you're looking for doesn't exist or has been deleted.</p>
          <div className="flex gap-3 justify-center">
            <Button
              variant="primary"
              onClick={() => onBack ? onBack() : navigate('/lead_list_filters')}
              icon={<ArrowLeft className="h-4 w-4" />}
            >
              Back to Lead List
            </Button>
          </div>
        </div>
      </div>
    );
  }





  const handleLogActivity = () => {
    navigate(`/log_activity/${id}`);
  };

  const handleEditLead = () => {
    if (onEdit) {
      onEdit(id);
    } else {
      navigate(`/manual_lead_entry?edit=${id}`);
    }
  };

  const handleExportLead = async () => {
    if (isExporting || !id) return;
    setIsExporting(true);
    const toastId = toast.loading('Preparing Excel export...');
    try {
      const exportName =
        leadData?.company ||
        [leadData?.firstName, leadData?.lastName].filter(Boolean).join(' ') ||
        leadData?.leadId ||
        'Lead';
      const result = await leadService.exportLeadDetail360(id, exportName);
      if (result.success) {
        toast.success('Lead details exported successfully', { id: toastId });
      } else {
        toast.error(result.error || 'Failed to export lead details', { id: toastId });
      }
    } catch (error) {
      toast.error(error.message || 'Failed to export lead details', { id: toastId });
    } finally {
      setIsExporting(false);
    }
  };


  const handleCallLead = async () => {
    if (!leadData?.phone) {
      showNotification('No phone number available for this lead', 'error');
      return;
    }

    try {
      showNotification('Initiating call...', 'info');
      const result = await leadMutations.initiateCall.mutateAsync(id);

      if (result.success) {
        showNotification(`Call initiated successfully to ${getLeadName(leadData)}`, 'success');
        handleTabChange('timeline');
      } else {
        showNotification(result.error || 'Failed to initiate call', 'error');
      }
    } catch (error) {
      showNotification('Failed to initiate call. Please try again.', 'error');
    }
  };

  const handleEmailLead = () => {
    if (leadData?.email) {
      setShowEmailModal(true);
    } else {
      showNotification('No email address available for this lead', 'error');
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim()) {
      showNotification('Please enter an email subject', 'error');
      return;
    }
    if (!emailHtml.trim()) {
      showNotification('Please enter an email message', 'error');
      return;
    }

    try {
      const result = await leadMutations.sendEmail.mutateAsync({
        leadId: id,
        emailData: {
          to: leadData?.email,
          subject: emailSubject.trim(),
          bodyHtml: emailHtml,
          bodyText: emailHtml.replace(/<[^>]*>/g, '') // Strip HTML for text version
        }
      });

      if (result.success) {
        showNotification('Email sent successfully!', 'success');
        setShowEmailModal(false);
        setEmailSubject('');
        setEmailHtml('');
        handleTabChange('timeline');
      } else {
        showNotification(result.error || 'Failed to send email', 'error');
      }
    } catch (error) {
      showNotification('Failed to send email. Please try again.', 'error');
    }
  };


  const handleScheduleMeeting = () => {
    if (leadData?.email) {
      setShowScheduleModal(true);
      // Reset errors when opening modal
      setMeetingErrors({});
    } else {
      showNotification('No email address available for this lead', 'error');
    }
  };

  const validateMeetingForm = () => {
    const newErrors = {};

    // Meeting title validation
    if (!meetingTitle.trim()) {
      newErrors.meetingTitle = 'Meeting title is required.';
    } else if (meetingTitle.trim().length > 200) {
      newErrors.meetingTitle = 'Meeting title cannot exceed 200 characters.';
    }

    // Meeting date validation
    if (!meetingDate) {
      newErrors.meetingDate = 'Meeting date is required.';
    } else {
      const selectedDate = new Date(meetingDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        newErrors.meetingDate = 'Meeting date cannot be in the past.';
      } else if (selectedDate.getTime() === today.getTime()) {
        // If date is today, validate time is not in the past
        if (meetingTime) {
          const [hours, minutes] = meetingTime.split(':');
          const selectedDateTime = new Date();
          selectedDateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
          const now = new Date();

          if (selectedDateTime < now) {
            newErrors.meetingTime = 'Meeting time cannot be in the past.';
          }
        }
      }
    }

    // Meeting time validation
    if (!meetingTime) {
      newErrors.meetingTime = 'Meeting time is required.';
    }

    // Meeting link validation
    if (!meetingLink.trim()) {
      newErrors.meetingLink = 'Meeting link is required.';
    } else {
      // Normalize URL - add https:// if no protocol is present
      let normalizedLink = meetingLink.trim();
      if (!normalizedLink.match(/^https?:\/\//i)) {
        normalizedLink = `https://${normalizedLink}`;
      }

      // Validate URL format
      try {
        new URL(normalizedLink);
      } catch (e) {
        newErrors.meetingLink = 'Please enter a valid meeting link URL.';
      }
    }

    // Duration validation - convert time format (HH:MM) to minutes
    if (!meetingDuration || !meetingDuration.trim()) {
      newErrors.meetingDuration = 'Meeting duration is required.';
    } else {
      const timePattern = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
      if (!timePattern.test(meetingDuration.trim())) {
        newErrors.meetingDuration = 'Please enter a valid duration in HH:MM format (e.g., 00:30 for 30 minutes).';
      } else {
        const [hours, minutes] = meetingDuration.trim().split(':').map(Number);
        const totalMinutes = hours * 60 + minutes;
        if (totalMinutes <= 0) {
          newErrors.meetingDuration = 'Meeting duration must be greater than 0 minutes.';
        } else if (totalMinutes > 480) {
          newErrors.meetingDuration = 'Meeting duration cannot exceed 8 hours (480 minutes).';
        }
      }
    }

    // Notes validation (optional but check length if provided)
    if (meetingNotes.trim().length > FIELD_LIMITS.notes) {
      newErrors.meetingNotes = `Notes cannot exceed ${FIELD_LIMITS.notes} characters.`;
    }

    setMeetingErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirmScheduleMeeting = async () => {
    // Validate form
    if (!validateMeetingForm()) {
      return;
    }

    try {
      const localDateTimeString = `${meetingDate}T${meetingTime}`;
      const scheduledStart = new Date(localDateTimeString);
      if (isNaN(scheduledStart.getTime())) {
        showNotification('Invalid date or time selected', 'error');
        return;
      }

      let normalizedMeetingLink = meetingLink.trim();
      if (!normalizedMeetingLink.match(/^https?:\/\//i)) {
        normalizedMeetingLink = `https://${normalizedMeetingLink}`;
      }

      const [hours, minutes] = meetingDuration.trim().split(':').map(Number);
      const durationMinutes = hours * 60 + minutes;

      const activityPayload = {
        type: 'meeting',
        title: meetingTitle.trim(),
        description: `${meetingNotes.trim() || 'Meeting scheduled via Quick Actions.'}`,
        duration: durationMinutes,
        scheduledFor: scheduledStart.toISOString(),
        meetingLink: normalizedMeetingLink,
        notes: meetingNotes.trim() || undefined,
        outcome: 'scheduled',
        priority: 'medium'
      };

      const result = await leadMutations.scheduleMeeting.mutateAsync({
        leadId: id,
        activityData: activityPayload
      });

      if (result.success) {
        showNotification('Meeting scheduled and invite sent successfully!', 'success');
        setShowScheduleModal(false);
        setMeetingTitle('');
        setMeetingNotes('');
        setMeetingDate('');
        setMeetingTime('');
        setMeetingDuration('00:30');
        setMeetingLink('');
        setMeetingType('video');
        setMeetingErrors({});
        handleTabChange('timeline');
      } else {
        showNotification(result.error || 'Failed to schedule meeting. Please try again.', 'error');
      }
    } catch (error) {
      showNotification('Failed to schedule meeting. Please try again.', 'error');
    }
  };

  const handleViewActivity = (activityId) => {
    if (!activityId) return;
    navigate(`/activity_detail/${activityId}`);
  };

  return (
    <div className="space-y-6">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg max-w-sm ${notification.type === 'success'
          ? 'bg-green-500 text-white'
          : notification.type === 'error'
            ? 'bg-red-500 text-white'
            : notification.type === 'info'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-500 text-white'
          }`}>
          <div className="flex items-center">
            <div className="flex-1">
              <div className="text-sm font-medium">{notification.message}</div>
            </div>
            <button
              onClick={() => setNotification(null)}
              className="ml-4 text-white hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col space-y-4">
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => onBack ? onBack() : navigate(-1)}
            className="flex-shrink-0"
          >
            Back
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 break-words capitalize">{getLeadName(leadData)}</h1>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <p className="text-sm sm:text-base text-gray-600 break-words first-letter:capitalize">{leadData.company || 'No company'}</p>
              {leadData.leadId && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-500 border border-gray-200 uppercase tracking-wider">
                  ID: {leadData.leadId}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <PermissionGate permission="lead_management" action="read">
              <Button
                variant="outline"
                icon={<Download className="h-4 w-4" />}
                onClick={handleExportLead}
                loading={isExporting}
                disabled={isExporting}
                className="shadow-sm whitespace-nowrap"
              >
                {isExporting ? 'Exporting...' : 'Export'}
              </Button>
            </PermissionGate>
            <PermissionGate permission="lead_management" action="update">
              <Button
                variant="primary"
                icon={<Edit className="h-4 w-4" />}
                onClick={handleEditLead}
              >
                Edit Lead
              </Button>
            </PermissionGate>
          </div>
        </div>
      </div>

      {/* Lead Info Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Basic Info */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Basic Information</h3>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <User className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 break-words capitalize">{getLeadName(leadData)}</p>
                <p className="text-xs text-gray-500">Contact Person</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Building className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 break-words first-letter:capitalize">{leadData.company || 'No company'}</p>
                <p className="text-xs text-gray-500">Company</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 break-words">{leadData.email || 'No email'}</p>
                <p className="text-xs text-gray-500">Email</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Phone className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 break-words">{leadData.phone || 'No phone'}</p>
                <p className="text-xs text-gray-500">Phone</p>
              </div>
            </div>
            {leadData.alternatePhone && (
              <div className="flex items-center space-x-3">
                <Phone className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 break-words">{leadData.alternatePhone}</p>
                  <p className="text-xs text-gray-500">Alternate Phone</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Lead Status */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Lead Status</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm text-gray-500">Score:</span>
              <span className={`inline-flex items-center px-2 py-0.5 sm:px-2.5 rounded-full text-xs font-medium ${getScoreColor(leadData.scoreLabel || 'Cold')}`}>
                {leadData.score !== null && leadData.score !== undefined ? `${leadData.score}` : 'N/A'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm text-gray-500">Status:</span>
              <span className={`inline-flex items-center px-2 py-0.5 sm:px-2.5 rounded-full text-xs font-medium capitalize ${getStatusColor(getStatusDisplay(leadData.stage))}`}>
                {getStatusDisplay(leadData.stage)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm text-gray-500">Owner:</span>
              <div className="text-right">
                <p className="text-xs sm:text-sm font-medium text-gray-900 break-words first-letter:capitalize">
                  {leadData.owner || (leadData.assignedTo ? getPerformerName(leadData.assignedTo) : 'Unassigned')}
                </p>
                {(leadData.ownerEmail || (leadData.assignedTo && typeof leadData.assignedTo === 'object' && leadData.assignedTo.email)) && (
                  <p className="text-xs text-gray-500 break-words">
                    {leadData.ownerEmail || leadData.assignedTo?.email}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm text-gray-500">Source:</span>
              <span className="text-xs sm:text-sm font-medium text-gray-900 break-words capitalize" title={mapSourceToDisplay(leadData.source, masterSources)}>{mapSourceToDisplay(leadData.source, masterSources)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm text-gray-500">Company Source:</span>
              <span className="text-xs sm:text-sm font-medium text-gray-900 break-words capitalize">{leadData.companySource || 'agrosaf'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm text-gray-500">Category:</span>
              <span className="text-xs sm:text-sm font-medium text-gray-900 break-words first-letter:capitalize">
                {leadData.category?.name || 'N/A'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm text-gray-500">Value:</span>
              <span className="text-xs sm:text-sm font-medium text-gray-900">₹{formatIndianAmount(leadData.leadValue || leadData.budget)}</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2 sm:gap-3">
            <PermissionGate permission="lead_management" action="read">
              <Button
                variant="outline"
                size="sm"
                icon={<Mail className="h-3 w-3 sm:h-4 sm:w-4" />}
                onClick={handleEmailLead}
                className="w-full justify-start text-xs sm:text-sm"
              >
                Send Email
              </Button>
            </PermissionGate>
            <PermissionGate permission="lead_management" action="update">
              <Button
                variant="outline"
                size="sm"
                icon={<Calendar className="h-3 w-3 sm:h-4 sm:w-4" />}
                onClick={handleScheduleMeeting}
                className="w-full justify-start text-xs sm:text-sm"
              >
                Schedule Meeting
              </Button>
            </PermissionGate>
          </div>
        </div>
      </div>

      {/* Lost Reason Section */}
      {lostReasonData && leadData?.stage === 'closed_lost' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 sm:p-6 shadow-sm">
          <div className="flex items-start space-x-3">
            <div className="bg-red-100 p-2 rounded-full flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-red-900">
                Loss Reason
              </h3>
              <div className="mt-1 flex flex-col">
                <p className="text-sm sm:text-base text-red-700 break-words flex items-start first-letter:capitalize">
                  {lostReasonData.reason}
                </p>
                {lostReasonData.date && (
                  <span className="text-xs text-red-500 mt-0.5">
                    {formatDate(lostReasonData.date)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex overflow-x-auto px-4 sm:px-6">
            <div className="flex space-x-4 sm:space-x-8 min-w-max">
              {[
                { key: 'profile', label: 'Profile' },
                { key: 'timeline', label: 'Activity' },
                { key: 'assignment_history', label: 'Assignment History' },
                { key: 'remarks', label: 'Remarks' },
                { key: 'documents', label: 'Documents' },
                { key: 'status_history', label: 'Status History' }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${activeTab === tab.key
                    ? 'border-agrosaf-green text-agrosaf-green'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </nav>
        </div>

        <div className="p-4 sm:p-6">
          {activeTab === 'timeline' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Activity Timeline</h3>
                <Button
                  variant="primary"
                  icon={<Plus className="h-4 w-4" />}
                  onClick={() => navigate(`/log_activity/${id}`)}
                  className="bg-agrosaf-green hover:bg-agrosaf-darkGreen whitespace-nowrap self-start sm:self-auto"
                >
                  Add Activity
                </Button>
              </div>

              {/* KPI Metrics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Follow-up Status</span>
                    <Clock className="h-4 w-4 text-blue-500" />
                  </div>
                  <div className="text-sm font-semibold text-gray-900 capitalize">
                    {latestFollowUpStatus || 'N/A'}
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Next Follow-up</span>
                    <Calendar className="h-4 w-4 text-purple-500" />
                  </div>
                  <div className="text-sm font-semibold text-gray-900">
                    {nextFollowUpDate ? formatDate(nextFollowUpDate, { includeTime: true }) : 'N/A'}
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Last Contact</span>
                    <Mail className="h-4 w-4 text-green-500" />
                  </div>
                  <div className="text-sm font-semibold text-gray-900">
                    {lastContactDate ? stringUtils.formatDate(lastContactDate, { includeSeconds: true }) : 'N/A'}
                  </div>
                </div>
              </div>

              {activitiesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading activities...</p>
                  </div>
                </div>
              ) : activities.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No activities logged yet</p>
                  <p className="text-sm text-gray-500 mt-2">Activities will appear here once logged</p>
                  <PermissionGate permission="lead_management" action="create">
                    <Button
                      variant="primary"
                      icon={<Plus className="h-4 w-4" />}
                      onClick={handleLogActivity}
                      className="mt-4 bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                    >
                      Log First Activity
                    </Button>
                  </PermissionGate>
                </div>
              ) : (
                <div className="relative">
                  {/* Timeline */}
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                  <div className="space-y-6">
                    {activities.map((activity, index) => {
                      const ActivityIcon = getActivityIcon(activity.type);
                      const iconColor = getActivityColor(activity.type);
                      const bgColor = getActivityBgColor(activity.type);

                      return (
                        <div
                          key={activity._id || activity.activityId}
                          className="relative pl-12 cursor-pointer"
                          onClick={() => handleViewActivity(activity._id || activity.activityId)}
                        >
                          {/* Timeline dot */}
                          <div className={`absolute left-0 top-1 w-8 h-8 rounded-full ${bgColor} flex items-center justify-center border-2 border-white shadow-sm`}>
                            <ActivityIcon className={`h-4 w-4 ${iconColor}`} />
                          </div>

                          {/* Activity card */}
                          <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className={`text-base sm:text-lg font-semibold text-gray-900 first-letter:capitalize ${activity.type === 'email' ? 'truncate' : ''
                                    }`}>
                                    {activity.title}
                                  </h4>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${activity.type === 'call' ? 'bg-blue-100 text-blue-800' :
                                    activity.type === 'email' ? 'bg-green-100 text-green-800' :
                                      activity.type === 'whatsapp' ? 'bg-green-100 text-green-800' :
                                        activity.type === 'meeting' ? 'bg-purple-100 text-purple-800' :
                                          'bg-gray-100 text-gray-800'
                                    }`}>
                                    {activity.type}
                                  </span>
                                  {activity.isAutomated && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                      Auto
                                    </span>
                                  )}
                                  {activity.type === 'call' && activity.callMetadata?.status && (
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getCallStatusColor(activity.callMetadata.status)}`}>
                                      {getCallStatusDisplay(activity.callMetadata.status)}
                                    </span>
                                  )}
                                </div>

                                {activity.description && (
                                  <p className={`text-sm text-gray-600 mb-3 break-words first-letter:capitalize ${activity.type === 'email' ? 'line-clamp-2' : 'whitespace-pre-wrap'
                                    }`}>
                                    {activity.description}
                                  </p>
                                )}

                                <div className="flex flex-wrap items-center gap-4 text-xs sm:text-sm text-gray-500">
                                  <div className="flex items-center gap-1">
                                    <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                                    <span>{formatDate(activity.createdAt || getActivityTime(activity))}</span>
                                  </div>

                                  {(() => {
                                    // Show duration for calls (talkDuration) or meetings (duration in minutes)
                                    if (activity.type === 'call') {
                                      // ONLY show actual talk time (talkDuration) - no fallbacks
                                      const talkDuration = activity.callMetadata?.talkDuration;
                                      return talkDuration !== null && talkDuration !== undefined && talkDuration > 0 ? (
                                        <div className="flex items-center gap-1">
                                          <span>Duration: {formatDuration(null, talkDuration)}</span>
                                        </div>
                                      ) : null;
                                    } else if (activity.type === 'meeting' && activity.duration) {
                                      // Show meeting duration
                                      return (
                                        <div className="flex items-center gap-1">
                                          <span>Duration: {formatDuration(activity.duration)}</span>
                                        </div>
                                      );
                                    }
                                    return null;
                                  })()}

                                  {activity.performedBy && (
                                    <div className="flex items-center gap-1">
                                      <User className="h-3 w-3 sm:h-4 sm:w-4" />
                                      <span>{getPerformerName(activity.performedBy)}</span>
                                    </div>
                                  )}

                                  {(activity.activityStatus?.name || activity.outcome) && (
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${(activity.activityStatus?.name?.toLowerCase() || activity.outcome) === 'completed' ? 'bg-green-100 text-green-800' :
                                      (activity.activityStatus?.name?.toLowerCase() || activity.outcome) === 'sent' ? 'bg-green-100 text-green-800' :
                                        (activity.activityStatus?.name?.toLowerCase() || activity.outcome) === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                                          (activity.activityStatus?.name?.toLowerCase() || activity.outcome) === 'cancelled' ? 'bg-red-100 text-red-800' :
                                            'bg-gray-100 text-gray-800'
                                      }`}>
                                      {activity.activityStatus?.name || activity.outcome}
                                    </span>
                                  )}
                                </div>

                                {activity.scheduledFor && (
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="flex items-center gap-2 text-sm text-blue-600 mb-2">
                                      <Calendar className="h-4 w-4" />
                                      <span>Activity Date & Time: {formatDate(activity.scheduledFor)}</span>
                                    </div>
                                    {activity.meetingLink && (
                                      <div className="mt-2">
                                        {(() => {
                                          // Check if meeting is in the past (more than 10 minutes after scheduled time)
                                          const scheduledTime = new Date(activity.scheduledFor);
                                          const meetingEndTime = new Date(scheduledTime.getTime() + (activity.duration || 30) * 60 * 1000);
                                          const now = new Date();
                                          const tenMinutesAfterEnd = new Date(meetingEndTime.getTime() + 10 * 60 * 1000);
                                          const isPastMeeting = now > tenMinutesAfterEnd;

                                          if (isPastMeeting) {
                                            return (
                                              <button
                                                disabled
                                                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-400 text-white rounded-lg cursor-not-allowed text-sm font-medium opacity-60"
                                                title="This meeting has ended"
                                              >
                                                <Calendar className="h-4 w-4" />
                                                Join Meeting (Expired)
                                              </button>
                                            );
                                          }

                                          return (
                                            <a
                                              href={activity.meetingLink}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                                            >
                                              <Calendar className="h-4 w-4" />
                                              Join Meeting
                                            </a>
                                          );
                                        })()}
                                      </div>
                                    )}
                                  </div>
                                )}

                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {activitiesHasMore && (
                      <div className="flex justify-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={loadMoreActivities}
                          disabled={activitiesLoadingMore}
                        >
                          {activitiesLoadingMore ? 'Loading...' : 'View more'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Complete Lead Profile</h3>

              {/* Personal Information */}
              <div className="border-b border-gray-200 pb-4">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Personal Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">First Name</label>
                    <p className="text-sm text-gray-900 mt-1 break-words capitalize">{leadData.firstName || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Last Name</label>
                    <p className="text-sm text-gray-900 mt-1 break-words capitalize">{leadData.lastName || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <p className="text-sm text-gray-900 mt-1 break-words">{leadData.email || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Phone</label>
                    <p className="text-sm text-gray-900 mt-1 break-words">{leadData.phone || 'N/A'}</p>
                  </div>
                  {leadData.alternatePhone && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Alternate Phone</label>
                      <p className="text-sm text-gray-900 mt-1 break-words">{leadData.alternatePhone}</p>
                    </div>
                  )}
                  {leadData.who && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Persona / Who</label>
                      <p className="text-sm text-gray-900 mt-1 capitalize break-words font-semibold">{leadData.who}</p>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium text-gray-500">Decision Maker</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">
                      {leadData.decisionMaker?.name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Lead Temperature</label>
                    <div className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${leadData.scoreLabel === 'hot' ? 'bg-red-100 text-red-800' :
                        leadData.scoreLabel === 'warm' ? 'bg-orange-100 text-orange-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                        {leadData.scoreLabel === 'hot' ? '🔥 Hot' :
                          leadData.scoreLabel === 'warm' ? '☀️ Warm' :
                            '❄️ Cold'
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Business Information */}
              <div className="border-b border-gray-200 pb-4">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Business Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Company Name</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">{leadData.company || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Website</label>
                    <p className="text-sm text-gray-900 mt-1 break-words">
                      {leadData.website ? (
                        <a href={leadData.website.startsWith('http') ? leadData.website : `https://${leadData.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline break-words">
                          {leadData.website}
                        </a>
                      ) : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Industry</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">
                      {leadData.industry?.name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Company Size</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">{leadData.companySize?.name || leadData.companySize || mapEmployeeCountToCompanySize(leadData.employeeCount)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Annual Revenue</label>
                    <p className="text-sm text-gray-900 mt-1 break-words">
                      {leadData.annualRevenue ? `₹${formatIndianAmount(leadData.annualRevenue)}` : 'N/A'}
                    </p>
                  </div>

                </div>
              </div>

              {/* Address Information */}
              <div className="border-b border-gray-200 pb-4">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Address Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-gray-500">Street Address</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">
                      {leadData.locations?.[0]?.address?.street ||
                        (typeof leadData.address === 'string' ? leadData.address : leadData.address?.street) ||
                        'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">City</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">
                      {leadData.locations?.[0]?.address?.city?.name ||
                        leadData.city ||
                        leadData.address?.city ||
                        'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">State</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">
                      {leadData.locations?.[0]?.address?.state?.name ||
                        leadData.state ||
                        leadData.address?.state ||
                        'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Postal Code</label>
                    <p className="text-sm text-gray-900 mt-1 break-words">
                      {leadData.locations?.[0]?.address?.postalCode ||
                        leadData.pincode ||
                        leadData.address?.pincode ||
                        'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Country</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">
                      {leadData.locations?.[0]?.country?.name ||
                        leadData.country ||
                        leadData.address?.country ||
                        'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Region</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">{leadData.region || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Lead Information */}
              <div className="border-b border-gray-200 pb-4">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Lead Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Source</label>
                    <p className="text-sm text-gray-900 mt-1 break-words">{mapSourceToDisplay(leadData.source, masterSources)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Company Source</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">{leadData.companySource || 'agrosaf'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Lead Category</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">
                      {leadData.category?.name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Expected Order Value</label>
                    <p className="text-sm text-gray-900 mt-1 break-words">
                      {leadData.budget ? `₹${formatIndianAmount(leadData.budget)}` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Lead Value</label>
                    <p className="text-sm text-gray-900 mt-1 break-words">
                      {(leadData.leadValue || leadData.budget) ? `₹${formatIndianAmount(leadData.leadValue || leadData.budget)}` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Timeline</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">{leadData.timeline || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Lead Video Status</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">
                      {leadData.leadVideoStatus?.name || (typeof leadData.leadVideoStatus === 'object' ? leadData.leadVideoStatus?.name : leadData.leadVideoStatus) || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Lead Order Status</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">
                      {leadData.leadOrderStatus?.name || (typeof leadData.leadOrderStatus === 'object' ? leadData.leadOrderStatus?.name : leadData.leadOrderStatus) || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Lead Call Status</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">
                      {leadData.leadCallStatus?.name || (typeof leadData.leadCallStatus === 'object' ? leadData.leadCallStatus?.name : leadData.leadCallStatus) || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Lead Interest</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">
                      {leadData.leadInterest?.name || (typeof leadData.leadInterest === 'object' ? leadData.leadInterest?.name : leadData.leadInterest) || 'N/A'}
                    </p>
                  </div>
                  {leadData.productRequirement && (
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-gray-500">Product/Requirement</label>
                      <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap break-words">{leadData.productRequirement}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Information */}
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-4">Additional Information</h4>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Description</label>
                    <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap break-words first-letter:capitalize">{leadData.notes || leadData.description || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Remarks</label>
                    <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap break-words first-letter:capitalize">{leadData.internalNotes || 'N/A'}</p>
                  </div>

                </div>
              </div>

              {/* Metadata */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Metadata</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Expanded Created By Info */}
                  {leadData.source !== 'linkedin' && (
                    <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg border border-gray-100">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 block">
                        Created By Information
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Name</p>
                          <p className="text-sm font-semibold text-gray-900 break-words">
                            {leadData.createdBy
                              ? (typeof leadData.createdBy === 'object'
                                ? `${leadData.createdBy.firstName || ''} ${leadData.createdBy.lastName || ''}`.trim() || leadData.createdBy.email || 'N/A'
                                : leadData.createdBy)
                              : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Employee ID</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {typeof leadData.createdBy === 'object' ? (leadData.createdBy?.employeeId || 'N/A') : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Role</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {typeof leadData.createdBy === 'object' && leadData.createdBy?.role
                              ? formatRoleName(leadData.createdBy.role)
                              : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Department</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {typeof leadData.createdBy === 'object' ? (leadData.createdBy?.department || 'N/A') : 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-500">Lead ID</label>
                    <p className="text-sm font-bold text-agrosaf-green mt-1 break-words">{leadData.leadId || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Created At</label>
                    <p className="text-sm text-gray-900 mt-1 break-words">{formatDate(leadData.createdAt)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Last Updated</label>
                    <p className="text-sm text-gray-900 mt-1 break-words">{formatDate(leadData.updatedAt || leadData.createdAt)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Last Activity</label>
                    <p className="text-sm text-gray-900 mt-1 break-words">{leadData.lastActivity || 'No activity'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'assignment_history' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Lead Assignment History</h3>
              {(!leadData.assignmentHistory || leadData.assignmentHistory.length === 0) ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No assignment history found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.No</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned By</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {[...leadData.assignmentHistory].reverse().map((history, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {leadData.assignmentHistory.length - index}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                            <div className="font-semibold text-agrosaf-green">{getPerformerName(history.assignedTo)}</div>
                            <div className="text-[12px] text-gray-500 flex flex-col">
                              {history.assignedTo?.employeeId && <span>ID: {history.assignedTo.employeeId}</span>}
                              {history.assignedTo?.email && <span>{history.assignedTo.email}</span>}
                              {history.assignedTo?.role && <span className="text-gray-500">{formatRoleName(history.assignedTo.role)}</span>}
                              {history.assignedTo?.department && <span className="text-gray-500">{history.assignedTo.department}</span>}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="font-semibold">{getPerformerName(history.assignedBy)}</div>
                            <div className="text-[12px] text-gray-500 flex flex-col">
                              {history.assignedBy?.employeeId && <span>ID: {history.assignedBy.employeeId}</span>}
                              {history.assignedBy?.email && <span>{history.assignedBy.email}</span>}
                              {history.assignedBy?.role && <span className="capitalize text-gray-500">{history.assignedBy.role.replace(/_/g, ' ')}</span>}
                              {history.assignedBy?.department && <span>{history.assignedBy.department}</span>}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(history.assignedAt, { includeTime: true, includeSeconds: false })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'status_history' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Lead Status History</h3>
              {statusHistory.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No status history found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.No</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From Stage</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To Stage</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Changed At</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Changed By</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {statusHistory.map((history, index) => {
                        const fromStageLabel = history?.fromStage?.name
                          || (typeof history?.fromStage === 'string' ? getStatusDisplay(history.fromStage) : 'N/A');
                        const toStageValue = history?.toStage?.value || history?.toStage;
                        const toStageLabel = history?.toStage?.name
                          || (typeof toStageValue === 'string' ? getStatusDisplay(toStageValue) : 'N/A');
                        const fullReason = history?.reason?.trim() || '';
                        const reasonPreview = fullReason.length > STATUS_REASON_PREVIEW_LIMIT
                          ? `${fullReason.slice(0, STATUS_REASON_PREVIEW_LIMIT)}...`
                          : fullReason;

                        return (
                          <tr key={history?._id || `${history?.changedAt || 'status'}-${index}`} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {index + 1}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {fromStageLabel}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {toStageLabel}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700 max-w-md">
                              {fullReason ? (
                                <p
                                  className="truncate max-w-md capitalize"
                                  title={fullReason}
                                >
                                  {reasonPreview}
                                </p>
                              ) : (
                                <p>N/A</p>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {history?.changedAt ? formatDate(history.changedAt, { includeTime: true, includeSeconds: false }) : 'N/A'}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              <div className="font-semibold text-gray-900">{getPerformerName(history?.changedBy)}</div>
                              <div className="text-[12px] text-gray-500 flex flex-col">
                                {history?.changedBy?.employeeId && <span>ID: {history.changedBy.employeeId}</span>}
                                {history?.changedBy?.email && <span>{history.changedBy.email}</span>}
                                {history?.changedBy?.role && <span className="capitalize">{history.changedBy.role.replace(/_/g, ' ')}</span>}
                                {history?.changedBy?.department && <span>{history.changedBy.department}</span>}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedStatusHistory(history);
                                  setShowStatusHistoryModal(true);
                                }}
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Lead Documents</h3>
                <span className="text-sm text-gray-500">
                  {Math.max((leadData.companyDocuments?.length || 0), (leadData.companyPdf ? 1 : 0))} Document(s)
                </span>
              </div>

              {((leadData.companyDocuments && leadData.companyDocuments.length > 0) || leadData.companyPdf) ? (
                (() => {
                  const documents = (leadData.companyDocuments || []).map((doc, idx) => ({ doc, idx }));
                  const videoDocuments = documents.filter(({ doc }) => doc.fileType === 'video' || !!doc.videoUrl);
                  const regularDocuments = documents.filter(({ doc }) => !(doc.fileType === 'video' || !!doc.videoUrl));

                  return (
                    <div className="space-y-6">
                      {videoDocuments.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {videoDocuments.map(({ doc, idx }) => {
                            const videoUrl = doc.videoUrl || (doc.documentMethod === 'link' ? doc.documentLink : '');
                            const isApproved = doc.verificationStatus === 'approved';
                            const isRejected = doc.verificationStatus === 'rejected';
                            const isPending = !isApproved && !isRejected;
                            const currentUserId = user?._id || user?.id || user?.userId || user?.user;
                            const assignedVerifier = doc.verificationAssignedTo;
                            const assignedVerifierId = assignedVerifier?._id || assignedVerifier?.id || assignedVerifier || '';
                            const assignedVerifierName = assignedVerifier && typeof assignedVerifier === 'object'
                              ? `${assignedVerifier.firstName || ''} ${assignedVerifier.lastName || ''}`.trim() || assignedVerifier.email || 'Assigned decision maker'
                              : '';
                            const assignedVerifierEmail = assignedVerifier && typeof assignedVerifier === 'object'
                              ? assignedVerifier.email || ''
                              : '';
                            const currentUserEmail = (user?.email || '').trim().toLowerCase();
                            const verifierEmail = (assignedVerifierEmail || '').trim().toLowerCase();
                            const isAssignedVerifier =
                              (assignedVerifierId && currentUserId && String(assignedVerifierId) === String(currentUserId)) ||
                              (verifierEmail && currentUserEmail && verifierEmail === currentUserEmail);
                            const canVerifyVideo = isPending && videoUrl && user?.role === 'super_admin' && isAssignedVerifier;
                            const statusClasses = isApproved
                              ? 'bg-green-100 text-green-700'
                              : isRejected
                                ? 'bg-red-100 text-red-700'
                                : 'bg-yellow-100 text-yellow-700';
                            const statusLabel = isApproved ? 'Approved' : isRejected ? 'Rejected' : 'Pending';

                            return (
                              <div key={idx} className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                                <div className="p-4 flex items-start justify-between bg-white border-b border-gray-200">
                                  <div className="flex items-center space-x-3">
                                    <div className="p-2 rounded-lg bg-red-100 text-red-600">
                                      <Video className="h-5 w-5" />
                                    </div>
                                    <div>
                                      <h4 className="text-sm font-bold text-gray-900 capitalize">
                                        {doc.documentTypeName || doc.documentType || 'Verification Video'}
                                      </h4>
                                      <p className="text-xs text-gray-500 uppercase tracking-wider">
                                        Video {doc.documentMethod || 'upload'}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${statusClasses}`}>
                                      {statusLabel}
                                    </span>
                                  </div>
                                </div>
                                <div className="p-4 grid grid-cols-2 gap-4">
                                  {doc.documentMethod === 'upload' && videoUrl && (
                                    <div className="col-span-2">
                                      <video src={videoUrl} controls className="w-full max-h-64 rounded-lg bg-black" />
                                    </div>
                                  )}
                                  <div>
                                    <p className="text-xs font-medium text-gray-500">Verification Status</p>
                                    <p className={`text-sm font-semibold mt-1 ${isApproved ? 'text-green-700' : isRejected ? 'text-red-700' : 'text-yellow-700'}`}>
                                      {isApproved ? 'Approved' : isRejected ? 'Rejected' : 'Pending approval'}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-xs font-medium text-gray-500">Approved At</p>
                                    <p className="text-sm text-gray-900 font-semibold mt-1">
                                      {doc.approvedAt ? formatDate(doc.approvedAt, { includeTime: true, includeSeconds: false }) : 'N/A'}
                                    </p>
                                  </div>
                                  <div className="col-span-2">
                                    <p className="text-xs font-medium text-gray-500">Decision Maker</p>
                                    <p className="text-sm text-gray-900 font-semibold mt-1 break-words">
                                      {assignedVerifierName || (assignedVerifierId ? 'Assigned decision maker' : 'Not assigned')}
                                    </p>
                                    {assignedVerifierEmail && (
                                      <p className="text-xs text-gray-500 mt-0.5 break-words">{assignedVerifierEmail}</p>
                                    )}
                                  </div>
                                  {isRejected && doc.rejectionReason && (
                                    <div className="col-span-2">
                                      <p className="text-xs font-medium text-gray-500">Rejection Reason</p>
                                      <p className="text-sm text-gray-900 font-medium mt-1">{doc.rejectionReason}</p>
                                    </div>
                                  )}
                                  {canVerifyVideo && (
                                    <div className="col-span-2 flex items-center justify-end gap-3 pt-2">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setRejectVideoDocumentIndex(idx);
                                          setVideoRejectionReason('');
                                        }}
                                        className="inline-flex items-center px-4 py-2 text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                                      >
                                        <XCircle className="h-4 w-4 mr-2" />
                                        Reject
                                      </button>
                                      <Button
                                        type="button"
                                        variant="primary"
                                        size="sm"
                                        onClick={() => handleApproveVideoVerification(idx)}
                                        className="inline-flex items-center"
                                      >
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                        Approve
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {(regularDocuments.length > 0 || (!leadData.companyDocuments || leadData.companyDocuments.length === 0) && leadData.companyPdf) && (
                        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                            <h4 className="text-sm font-semibold text-gray-900">Uploaded Documents</h4>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-white">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Certificate No.</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry</th>
                                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-100">
                                {regularDocuments.map(({ doc, idx }) => (
                                  <tr key={idx} className="hover:bg-gray-50">
                                    <td className="px-4 py-3">
                                      <div className="flex items-center gap-3">
                                        <div className={`p-2 rounded-lg ${doc.documentMethod === 'upload' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'}`}>
                                          {doc.documentMethod === 'upload' ? <FileText className="h-4 w-4" /> : <LinkIcon className="h-4 w-4" />}
                                        </div>
                                        <div>
                                          <p className="text-sm font-semibold text-gray-900 capitalize">{doc.documentTypeName || doc.documentType || 'Unnamed Document'}</p>
                                          <p className="text-xs text-gray-500">{doc.documentMethod === 'upload' ? 'Uploaded file' : 'External link'}</p>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-700 capitalize">{doc.documentMethod || 'upload'}</td>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{doc.certificateNumber || 'N/A'}</td>
                                    <td className={`px-4 py-3 text-sm font-medium ${doc.expiryDate && new Date(doc.expiryDate) < new Date() ? 'text-red-600' : 'text-gray-900'}`}>
                                      {doc.expiryDate ? formatDate(doc.expiryDate, { includeTime: false }) : 'N/A'}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      {doc.documentMethod === 'upload' && doc.companyPdf ? (
                                        <button
                                          type="button"
                                          onClick={() => handleDocumentDownload(doc)}
                                          className="inline-flex items-center justify-center p-2 text-agrosaf-green hover:bg-green-50 rounded-full transition-colors"
                                          title="Download Document"
                                        >
                                          <LinkIcon className="h-5 w-5" />
                                        </button>
                                      ) : doc.documentMethod === 'link' && doc.documentLink ? (
                                        <a
                                          href={doc.documentLink?.startsWith('http') ? doc.documentLink : `https://${doc.documentLink}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center justify-center p-2 text-agrosaf-green hover:bg-green-50 rounded-full transition-colors"
                                          title="Open Document"
                                        >
                                          <LinkIcon className="h-5 w-5" />
                                        </a>
                                      ) : (
                                        <span className="text-sm text-gray-400">-</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                                {(!leadData.companyDocuments || leadData.companyDocuments.length === 0) && leadData.companyPdf && (
                                  <tr className="hover:bg-gray-50">
                                    <td className="px-4 py-3">
                                      <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
                                          <FileText className="h-4 w-4" />
                                        </div>
                                        <div>
                                          <p className="text-sm font-semibold text-gray-900">Legacy Attachment</p>
                                          <p className="text-xs text-gray-500">Older lead document</p>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-700">Upload</td>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">N/A</td>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">N/A</td>
                                    <td className="px-4 py-3 text-right">
                                      <button
                                        type="button"
                                        onClick={() => handleDocumentDownload(null, leadData.companyPdf)}
                                        className="inline-flex items-center justify-center p-2 text-agrosaf-green hover:bg-green-50 rounded-full transition-colors"
                                        title="Download PDF"
                                      >
                                        <LinkIcon className="h-5 w-5" />
                                      </button>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()
              ) : (
                <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                  <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 font-medium">No documents have been uploaded for this lead.</p>
                  <p className="text-sm text-gray-400 mt-1">Uploaded certifications and documents will appear here.</p>
                </div>
              )}
            </div>
          )}
          {activeTab === 'remarks' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                <h3 className="text-lg font-semibold text-gray-900">Remarks & Comments</h3>
              </div>

              {/* Add Remark Form */}
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">Add a new remark</label>
                <div className="flex flex-col space-y-3">
                  <textarea
                    value={newRemark}
                    onChange={(e) => setNewRemark(e.target.value.slice(0, 3000))}
                    placeholder="Write your remark here..."
                    maxLength={3000}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-agrosaf-green focus:border-transparent min-h-[100px] text-sm"
                  />

                  {/* File Attachment Input / Preview */}
                  <div className="mt-1">
                    {isUploadingRemarkFile ? (
                      <div className="flex items-center space-x-3 p-3 bg-white border border-gray-200 rounded-lg shadow-sm max-w-md animate-pulse">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-agrosaf-green flex-shrink-0"></div>
                        <span className="text-xs font-semibold text-gray-600">Processing attachment in cloud...</span>
                      </div>
                    ) : !remarkFile ? (
                      <div className="flex items-center space-x-2">
                        <label className="flex items-center space-x-2 px-3 py-1.5 bg-white border border-gray-300 rounded-lg shadow-sm cursor-pointer hover:bg-gray-50 transition duration-150 ease-in-out text-sm text-gray-700">
                          <Paperclip className="h-4 w-4 text-gray-500" />
                          <span>Attach Image or PDF</span>
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                if (file.size > 10 * 1024 * 1024) {
                                  toast.error('File size must be less than 10MB');
                                  return;
                                }
                                setIsUploadingRemarkFile(true);
                                try {
                                  const uploadFormData = new FormData();
                                  uploadFormData.append('file', file);
                                  uploadFormData.append('folder', 'remark-attachments');

                                  const uploadRes = await post(API_ENDPOINTS.UPLOAD.CLOUDINARY, uploadFormData);
                                  if (uploadRes && uploadRes.responseCode === 200 && uploadRes.data) {
                                    const metadata = {
                                      name: file.name,
                                      url: uploadRes.data.url,
                                      public_id: uploadRes.data.public_id,
                                      size: file.size,
                                      mimetype: file.type,
                                      resource_type: uploadRes.data.resource_type || (file.type.startsWith('image/') ? 'image' : 'raw')
                                    };
                                    setRemarkFile(metadata);
                                    toast.success('File uploaded successfully');
                                  } else {
                                    throw new Error('Upload failed');
                                  }
                                } catch (err) {
                                  toast.error(err.message || 'File upload failed');
                                } finally {
                                  setIsUploadingRemarkFile(false);
                                }
                              }
                            }}
                          />
                        </label>
                        <span className="text-xs text-gray-400">Max 10MB (PDF or Image)</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg shadow-sm max-w-md">
                        <div className="flex items-center space-x-3 overflow-hidden flex-1">
                          {remarkFile.mimetype && remarkFile.mimetype.startsWith('image/') ? (
                            <a
                              href={remarkFile.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="h-10 w-10 rounded bg-gray-100 flex-shrink-0 overflow-hidden border border-gray-200 hover:opacity-90 transition block"
                            >
                              <img
                                src={remarkFile.url}
                                alt="preview"
                                className="h-full w-full object-cover cursor-zoom-in"
                              />
                            </a>
                          ) : (
                            <a
                              href={remarkFile.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="h-10 w-10 rounded bg-red-50 flex items-center justify-center flex-shrink-0 border border-red-100 hover:bg-red-100 transition block"
                            >
                              <FileText className="h-5 w-5 text-red-500" />
                            </a>
                          )}
                          <div className="overflow-hidden flex-1">
                            <p className="text-xs font-semibold text-gray-800 truncate" title={remarkFile.name}>{remarkFile.name}</p>
                            <div className="flex items-center space-x-2">
                              <span className="text-[10px] text-gray-400">{(remarkFile.size / 1024 / 1024).toFixed(2)} MB</span>
                              <span className="text-[10px] text-gray-300">•</span>
                              <a
                                href={remarkFile.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[10px] text-agrosaf-green hover:underline font-semibold"
                              >
                                View
                              </a>
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!remarkFile?.public_id) {
                              setRemarkFile(null);
                              return;
                            }
                            setIsUploadingRemarkFile(true);
                            try {
                              const publicId = remarkFile.public_id;
                              const resourceType = remarkFile.resource_type || 'raw';
                              const deleteRes = await del(`${API_ENDPOINTS.UPLOAD.DELETE}?public_id=${publicId}&resource_type=${resourceType}`);
                              if (deleteRes && (deleteRes.responseCode === 200 || deleteRes.success)) {
                                setRemarkFile(null);
                                toast.success('File removed from cloud');
                              } else {
                                throw new Error('Failed to delete file from cloud');
                              }
                            } catch (err) {
                              toast.error(err.message || 'File deletion failed');
                            } finally {
                              setIsUploadingRemarkFile(false);
                            }
                          }}
                          className="p-1 hover:bg-gray-100 rounded-full transition text-gray-500 hover:text-red-500 ml-2"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      {getCharacterCount(newRemark, 3000)}
                    </span>
                    <div className="flex justify-end">
                      <Button
                        variant="primary"
                        onClick={async () => {
                          if (!newRemark.trim()) {
                            toast.error('Remark cannot be empty');
                            return;
                          }
                          setIsSubmittingRemark(true);
                          try {
                            const result = await leadMutations.addRemark.mutateAsync({
                              leadId: id,
                              remark: newRemark.trim(),
                              file: remarkFile
                            });
                            if (result.success) {
                              toast.success('Remark added successfully');
                              setNewRemark('');
                              setRemarkFile(null);
                            } else {
                              toast.error(result.error || 'Failed to add remark');
                            }
                          } catch (err) {
                            toast.error(err.message || 'Failed to add remark');
                          } finally {
                            setIsSubmittingRemark(false);
                          }
                        }}
                        loading={isSubmittingRemark}
                        disabled={!newRemark.trim() || isSubmittingRemark || isUploadingRemarkFile}
                        className="px-6 py-2 rounded-lg shadow-sm"
                      >
                        Post Remark
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Remarks List */}
              <div className="space-y-4">
                {remarksLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-agrosaf-green"></div>
                    <span className="ml-3 text-sm text-gray-500">Loading remarks...</span>
                  </div>
                ) : paginatedRemarks.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
                    <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">No remarks yet. Be the first to add one!</p>
                  </div>
                ) : (
                  <>
                    {totalRemarksCount > 0 && (
                      <p className="text-xs text-gray-400">Showing {paginatedRemarks.length} of {totalRemarksCount} remarks</p>
                    )}
                    {paginatedRemarks.map((remark, index) => (
                      <div key={remark._id || index} className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="h-10 w-10 rounded-full bg-agrosaf-green/10 flex items-center justify-center">
                              <User className="h-5 w-5 text-agrosaf-green" />
                            </div>
                            <div>
                              <div className="text-sm font-bold text-gray-900">{getPerformerName(remark.user)}</div>
                              <div className="text-[11px] text-gray-500 flex flex-wrap gap-x-3">
                                {remark.user?.employeeId && <span>ID: {remark.user.employeeId}</span>}
                                {remark.user?.role && <span className="capitalize">{remark.user.role.replace(/_/g, ' ')}</span>}
                                {remark.user?.department && <span>{remark.user.department}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="text-xs text-gray-500 font-medium">
                            {formatDate(remark.createdAt, { includeTime: true })}
                          </div>
                        </div>
                        <div className="pl-12">
                          <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap first-letter:capitalize mb-2">
                            {renderRemarkText(remark.remark)}
                          </p>
                          {remark.file && remark.file.url && (
                            <div className="mt-3 flex items-start">
                              {remark.file.mimetype && remark.file.mimetype.startsWith('image/') ? (
                                <div className="relative group rounded-lg overflow-hidden border border-gray-200 bg-gray-50 max-w-xs shadow-sm hover:shadow transition duration-200">
                                  <a href={remark.file.url} target="_blank" rel="noopener noreferrer">
                                    <img
                                      src={remark.file.url}
                                      alt={remark.file.name || 'Attached Image'}
                                      className="max-h-48 w-auto object-contain cursor-zoom-in group-hover:opacity-90 transition"
                                    />
                                  </a>
                                  <div className="p-2 bg-white border-t border-gray-100 flex items-center justify-between text-xs">
                                    <span className="text-gray-600 truncate max-w-[150px]" title={remark.file.name}>{remark.file.name || 'image.jpg'}</span>
                                    <a
                                      href={remark.file.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-agrosaf-green hover:underline font-semibold ml-2"
                                    >
                                      View
                                    </a>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center space-x-3 p-3 bg-gray-50 border border-gray-200 rounded-lg shadow-sm hover:bg-gray-100 transition duration-150 max-w-sm">
                                  <div className="h-10 w-10 rounded bg-red-50 flex items-center justify-center flex-shrink-0 border border-red-100">
                                    <FileText className="h-5 w-5 text-red-500" />
                                  </div>
                                  <div className="overflow-hidden flex-1">
                                    <p className="text-xs font-bold text-gray-800 truncate" title={remark.file.name}>{remark.file.name || 'document.pdf'}</p>
                                    <p className="text-[10px] text-gray-500">
                                      {remark.file.size ? `${(remark.file.size / 1024 / 1024).toFixed(2)} MB` : 'PDF Document'}
                                    </p>
                                  </div>
                                  <a
                                    href={remark.file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-agrosaf-green font-semibold hover:underline ml-2"
                                  >
                                    View
                                  </a>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {remarksHasMore && (
                      <div className="flex justify-center pt-4">
                        <Button
                          variant="outline"
                          onClick={() => loadMoreRemarks()}
                          loading={remarksLoadingMore}
                          className="px-6 py-2 text-sm font-semibold rounded-xl shadow-sm transition"
                        >
                          Load More Remarks
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>


      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Send Email</h3>
              <button
                onClick={() => setShowEmailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  To
                </label>
                <input
                  type="email"
                  value={leadData?.email || ''}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => {
                    const sanitized = sanitizeTextInput(e.target.value);
                    setEmailSubject(sanitized.slice(0, 200));
                  }}
                  placeholder="Enter email subject"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                  maxLength={200}
                />
                <div className="flex justify-end mt-1">
                  <span className="text-xs text-gray-500">
                    {getCharacterCount(emailSubject, 200)}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden">
                  <ReactQuill
                    theme="snow"
                    value={emailHtml}
                    onChange={setEmailHtml}
                    placeholder="Enter your message here..."
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                        [{ 'color': [] }, { 'background': [] }],
                        ['link'],
                        ['clean']
                      ]
                    }}
                    style={{
                      minHeight: '200px',
                      backgroundColor: 'white'
                    }}
                  />
                  <style>{`
                    .ql-editor {
                      min-height: 200px;
                      padding-top: 12px !important;
                    }
                    .ql-editor.ql-blank::before {
                      top: 12px !important;
                      left: 15px !important;
                      font-style: normal;
                      color: #9ca3af;
                    }
                  `}</style>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowEmailModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                icon={<Send className="h-4 w-4" />}
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                onClick={handleSendEmail}
                disabled={leadMutations.sendEmail.isPending}
              >
                {leadMutations.sendEmail.isPending ? 'Sending...' : 'Send Email'}
              </Button>
            </div>
          </div>
        </div>
      )}


      {/* Schedule Meeting Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Schedule Meeting</h3>
              <button
                onClick={() => {
                  setShowScheduleModal(false);
                  // Reset errors when closing modal
                  setMeetingErrors({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meeting Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={meetingTitle}
                  onChange={(e) => {
                    const sanitized = sanitizeTextInput(e.target.value);
                    setMeetingTitle(sanitized.slice(0, 200));
                    // Clear error when user types
                    if (meetingErrors.meetingTitle) {
                      setMeetingErrors(prev => ({ ...prev, meetingTitle: undefined }));
                    }
                  }}
                  onBlur={() => {
                    // Validate on blur
                    if (!meetingTitle.trim()) {
                      setMeetingErrors(prev => ({ ...prev, meetingTitle: 'Meeting title is required.' }));
                    } else if (meetingTitle.trim().length > 200) {
                      setMeetingErrors(prev => ({ ...prev, meetingTitle: 'Meeting title cannot exceed 200 characters.' }));
                    }
                  }}
                  placeholder="Enter meeting title"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${meetingErrors.meetingTitle ? 'border-red-300' : 'border-gray-300'
                    }`}
                  maxLength={200}
                />
                <div className="flex justify-between items-center mt-1">
                  {meetingErrors.meetingTitle && (
                    <p className="text-sm text-red-600">{meetingErrors.meetingTitle}</p>
                  )}
                  <span className="text-xs text-gray-500 ml-auto">
                    {getCharacterCount(meetingTitle, 200)}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={meetingDate}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      const today = new Date().toISOString().split('T')[0];

                      // Prevent selecting past dates
                      if (selectedDate < today) {
                        setMeetingErrors(prev => ({ ...prev, meetingDate: 'Meeting date cannot be in the past.' }));
                        return;
                      }

                      setMeetingDate(selectedDate);
                      // Clear error when user selects valid date
                      if (meetingErrors.meetingDate) {
                        setMeetingErrors(prev => ({ ...prev, meetingDate: undefined }));
                      }
                    }}
                    onBlur={() => {
                      // Validate on blur
                      if (!meetingDate) {
                        setMeetingErrors(prev => ({ ...prev, meetingDate: 'Meeting date is required.' }));
                      } else {
                        const selectedDate = new Date(meetingDate);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (selectedDate < today) {
                          setMeetingErrors(prev => ({ ...prev, meetingDate: 'Meeting date cannot be in the past.' }));
                        }
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${meetingErrors.meetingDate ? 'border-red-300' : 'border-gray-300'
                      }`}
                  />
                  {meetingErrors.meetingDate && (
                    <p className="text-sm text-red-600 mt-1">{meetingErrors.meetingDate}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={meetingTime}
                    onChange={(e) => {
                      const selectedTime = e.target.value;

                      // If date is today, validate time is not in the past
                      if (meetingDate) {
                        const selectedDate = new Date(meetingDate);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const isToday = selectedDate.getTime() === today.getTime();

                        if (isToday && selectedTime) {
                          const [hours, minutes] = selectedTime.split(':');
                          const selectedDateTime = new Date();
                          selectedDateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
                          const now = new Date();

                          if (selectedDateTime < now) {
                            setMeetingErrors(prev => ({ ...prev, meetingTime: 'Meeting time cannot be in the past.' }));
                            return;
                          }
                        }
                      }

                      setMeetingTime(selectedTime);
                      // Clear error when user selects valid time
                      if (meetingErrors.meetingTime) {
                        setMeetingErrors(prev => ({ ...prev, meetingTime: undefined }));
                      }
                    }}
                    onBlur={() => {
                      // Validate on blur
                      if (!meetingTime) {
                        setMeetingErrors(prev => ({ ...prev, meetingTime: 'Meeting time is required.' }));
                      } else if (meetingDate) {
                        // Check if date+time combination is in the past
                        const selectedDate = new Date(meetingDate);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const isToday = selectedDate.getTime() === today.getTime();

                        if (isToday) {
                          const [hours, minutes] = meetingTime.split(':');
                          const selectedDateTime = new Date();
                          selectedDateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
                          const now = new Date();

                          if (selectedDateTime < now) {
                            setMeetingErrors(prev => ({ ...prev, meetingTime: 'Meeting time cannot be in the past.' }));
                          }
                        }
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${meetingErrors.meetingTime ? 'border-red-300' : 'border-gray-300'
                      }`}
                  />
                  {meetingErrors.meetingTime && (
                    <p className="text-sm text-red-600 mt-1">{meetingErrors.meetingTime}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={meetingDuration}
                    onChange={(e) => {
                      const selectedDuration = e.target.value;
                      setMeetingDuration(selectedDuration);
                      // Clear error when user enters duration
                      if (meetingErrors.meetingDuration) {
                        setMeetingErrors(prev => ({ ...prev, meetingDuration: undefined }));
                      }
                    }}
                    onBlur={() => {
                      // Validate on blur
                      if (!meetingDuration || !meetingDuration.trim()) {
                        setMeetingErrors(prev => ({ ...prev, meetingDuration: 'Meeting duration is required.' }));
                      } else {
                        const timePattern = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
                        if (!timePattern.test(meetingDuration.trim())) {
                          setMeetingErrors(prev => ({ ...prev, meetingDuration: 'Please enter a valid duration in HH:MM format (e.g., 00:30 for 30 minutes).' }));
                        } else {
                          const [hours, minutes] = meetingDuration.trim().split(':').map(Number);
                          const totalMinutes = hours * 60 + minutes;
                          if (totalMinutes <= 0) {
                            setMeetingErrors(prev => ({ ...prev, meetingDuration: 'Meeting duration must be greater than 0 minutes.' }));
                          } else if (totalMinutes > 480) {
                            setMeetingErrors(prev => ({ ...prev, meetingDuration: 'Meeting duration cannot exceed 8 hours (480 minutes).' }));
                          }
                        }
                      }
                    }}
                    step="60"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${meetingErrors.meetingDuration ? 'border-red-300' : 'border-gray-300'
                      }`}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter duration in HH:MM format (e.g., 00:30 for 30 minutes, 01:00 for 1 hour)
                  </p>
                  {meetingErrors.meetingDuration && (
                    <p className="text-sm text-red-600 mt-1">{meetingErrors.meetingDuration}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meeting Link <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={meetingLink}
                  onChange={(e) => {
                    // Only remove dangerous HTML characters, allow all URL characters
                    const sanitized = e.target.value.replace(/[<>]/g, '');
                    setMeetingLink(sanitized.slice(0, 500));
                    // Clear error when user types
                    if (meetingErrors.meetingLink) {
                      setMeetingErrors(prev => ({ ...prev, meetingLink: undefined }));
                    }
                  }}
                  onBlur={() => {
                    // Validate on blur
                    if (!meetingLink.trim()) {
                      setMeetingErrors(prev => ({ ...prev, meetingLink: 'Meeting link is required.' }));
                    } else {
                      // Normalize URL - add https:// if no protocol is present
                      let normalizedLink = meetingLink.trim();
                      if (!normalizedLink.match(/^https?:\/\//i)) {
                        normalizedLink = `https://${normalizedLink}`;
                      }
                      // Validate URL format
                      try {
                        new URL(normalizedLink);
                      } catch (e) {
                        setMeetingErrors(prev => ({ ...prev, meetingLink: 'Please enter a valid meeting link URL.' }));
                      }
                    }
                  }}
                  placeholder="meet.google.com/roq-mxzg-rpw or https://zoom.us/j/..."
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${meetingErrors.meetingLink ? 'border-red-300' : 'border-gray-300'
                    }`}
                  maxLength={500}
                />
                <div className="flex justify-between items-center mt-1">
                  {meetingErrors.meetingLink && (
                    <p className="text-sm text-red-600">{meetingErrors.meetingLink}</p>
                  )}
                  <p className="text-xs text-gray-500 ml-auto">
                    Enter the video conferencing link (Zoom, Google Meet, Teams, etc.) - protocol optional
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  rows={4}
                  value={meetingNotes}
                  onChange={(e) => {
                    const sanitized = sanitizeTextInput(e.target.value);
                    setMeetingNotes(sanitized.slice(0, FIELD_LIMITS.notes));
                    // Clear error when user types
                    if (meetingErrors.meetingNotes) {
                      setMeetingErrors(prev => ({ ...prev, meetingNotes: undefined }));
                    }
                  }}
                  onBlur={() => {
                    // Validate on blur
                    if (meetingNotes.trim().length > FIELD_LIMITS.notes) {
                      setMeetingErrors(prev => ({ ...prev, meetingNotes: `Notes cannot exceed ${FIELD_LIMITS.notes} characters.` }));
                    }
                  }}
                  placeholder="Add any additional notes or agenda items..."
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${meetingErrors.meetingNotes ? 'border-red-300' : 'border-gray-300'
                    }`}
                  maxLength={FIELD_LIMITS.notes}
                />
                <div className="flex justify-between items-center mt-1">
                  {meetingErrors.meetingNotes && (
                    <p className="text-sm text-red-600">{meetingErrors.meetingNotes}</p>
                  )}
                  <span className="text-xs text-gray-500 ml-auto">
                    {getCharacterCount(meetingNotes, FIELD_LIMITS.notes)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowScheduleModal(false);
                  // Reset errors when canceling
                  setMeetingErrors({});
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                icon={<Calendar className="h-4 w-4" />}
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                onClick={handleConfirmScheduleMeeting}
                disabled={leadMutations.scheduleMeeting.isPending}
              >
                {leadMutations.scheduleMeeting.isPending ? 'Scheduling...' : 'Schedule Meeting'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {rejectVideoDocumentIndex !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">Reject Video Verification</h2>
              <button
                type="button"
                onClick={() => {
                  setRejectVideoDocumentIndex(null);
                  setVideoRejectionReason('');
                }}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                This will mark the selected verification video as rejected.
              </p>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Rejection Reason <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <textarea
                value={videoRejectionReason}
                onChange={(e) => setVideoRejectionReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
              />
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setRejectVideoDocumentIndex(null);
                  setVideoRejectionReason('');
                }}
                className="px-4 py-2 text-gray-900 font-semibold border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRejectVideoVerification}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors"
              >
                <XCircle className="h-4 w-4" />
                Reject Video
              </button>
            </div>
          </div>
        </div>
      )}

      {showStatusHistoryModal && selectedStatusHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Status History Details</h3>
              <button
                type="button"
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                onClick={() => {
                  setShowStatusHistoryModal(false);
                  setSelectedStatusHistory(null);
                }}
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">From Stage</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedStatusHistory?.fromStage?.name
                      || (typeof selectedStatusHistory?.fromStage === 'string' ? getStatusDisplay(selectedStatusHistory.fromStage) : 'N/A')}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">To Stage</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedStatusHistory?.toStage?.name
                      || getStatusDisplay(selectedStatusHistory?.toStage?.value || selectedStatusHistory?.toStage)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Changed At</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedStatusHistory?.changedAt
                      ? formatDate(selectedStatusHistory.changedAt, { includeTime: true, includeSeconds: false })
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Changed By</label>
                  <p className="text-sm text-gray-900 mt-1">{getPerformerName(selectedStatusHistory?.changedBy)}</p>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <label className="text-sm font-medium text-gray-500">Changed By Details</label>
                <div className="mt-2 text-sm text-gray-800 space-y-1">
                  <p><span className="font-medium">Employee ID:</span> {selectedStatusHistory?.changedBy?.employeeId || 'N/A'}</p>
                  <p><span className="font-medium">Email:</span> {selectedStatusHistory?.changedBy?.email || 'N/A'}</p>
                  <p><span className="font-medium">Role:</span> {selectedStatusHistory?.changedBy?.role ? selectedStatusHistory.changedBy.role.replace(/_/g, ' ') : 'N/A'}</p>
                  <p><span className="font-medium">Department:</span> {selectedStatusHistory?.changedBy?.department || 'N/A'}</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Status Change Reason</label>
                <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap break-words bg-gray-50 p-4 rounded-lg border border-gray-200 first-letter:capitalize">
                  {selectedStatusHistory?.reason?.trim() || 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadDetail360;
